import { createApi } from "@reduxjs/toolkit/query/react";

import {
  IAwaitingRequest,
  IProduct,
  IProductConsumed,
  IProductRequest,
  IReturnRequest,
  ISubProduct,
  ISubProductItem,
  ITransferRequest,
  ProjectToolStatus,
  SubProductsItem,
  ToolStatus,
} from "@/interfaces/tools.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const toolsApi = createApi({
  reducerPath: "toolsApi",
  baseQuery: BaseWithAuth(),
  tagTypes: [
    "products",
    "single-product",
    "single-subProduct",
    "single-subProductItem",
    "subProductItems",
    "consumedProducts",
    "transferTools",
    "productRequest",
    "awaitingRequest",
    "returnRequest",
    "subProductList",
  ],
  endpoints: (builder) => ({
    //Get Products
    getProducts: builder.query<
      GeneralResponse<IProduct[]>,
      {
        filters?: string;
        id?: string;
      }
    >({
      query: ({ filters, id }) => {
        const basePath = `/workspace/${getWorkspace()}/tool/product`;

        if (id) {
          return `${basePath}/${id}`;
        }

        return filters ? `${basePath}${filters}` : basePath;
      },
      providesTags: ["products"],
    }),

    //Get Single Product
    getSingleProduct: builder.query<
      GeneralResponse<IProduct>,
      {
        id?: string;
        filters?: string;
      }
    >({
      query: ({ id, filters }) =>
        `/workspace/${getWorkspace()}/tool/product/${id ? id : ""}${filters ?? ""}`,
      providesTags: ["single-product"],
    }),

    //Get Consumed Products
    getConsumedProducts: builder.query<
      GeneralResponse<IProductConsumed[]>,
      {
        filters?: string;
      }
    >({
      query: ({ filters }) =>
        `/workspace/${getWorkspace()}/tool/product/consumed${filters ?? ""}`,
      providesTags: ["consumedProducts"],
    }),

    //Get single Sub Products
    getSingleSubProduct: builder.query<
      GeneralResponse<SubProductsItem | ISubProduct>,
      {
        id?: string;
        subProductId?: string;
      }
    >({
      query: ({ id, subProductId }) =>
        `/workspace/${getWorkspace()}/tool/product/${id}/sub-product/${subProductId}`,
      providesTags: ["single-subProduct"],
    }),

    //Get all subProductItems for single sub Product
    getSubProductItems: builder.query<
      GeneralResponse<ISubProductItem[]> & {
        statusCounts?: {
          available: number;
          consumed: number;
          total: number;
          repair: number;
        };
      },
      {
        productId: string;
        subProductId: string;
      }
    >({
      query: ({ productId, subProductId }) =>
        `/workspace/${getWorkspace()}/tool/product/${productId}/sub-product/${subProductId}/sub-product-item`,
      providesTags: ["subProductItems"],
    }),

    //get single subProductItem
    getSingleSubProductItem: builder.query<
      GeneralResponse<ISubProductItem>,
      {
        productId: string;
        subProductId: string;
        itemId: string;
      }
    >({
      query: ({ productId, subProductId, itemId }) =>
        `/workspace/${getWorkspace()}/tool/product/${productId}/sub-product/${subProductId}/sub-product-item/${itemId}`,
      providesTags: ["single-subProductItem"],
    }),

    //get sub-product list for particular product
    getSubProductList: builder.query<
      GeneralResponse<ISubProduct[]>,
      { filters?: string; productId: string; status?: ToolStatus }
    >({
      query: ({ filters, productId, status }) => {
        return `workspace/${getWorkspace()}/tool/product/${productId}/sub-product?${status ? `status=${status}` : ""}${filters ? `&${filters}` : ""}`;
      },
      providesTags: ["subProductList"],
    }),

    //Mutate Products
    mutateProducts: builder.mutation<
      object,
      {
        data?: object;
        productId?: string;
        method: "POST" | "PATCH" | "DELETE" | "PUT";
      }
    >({
      query: ({ data, productId, method }) => ({
        url: `workspace/${getWorkspace()}/tool/product/${productId ? `${productId}` : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["products", "single-product"],
    }),

    //Mutate SubProducts
    mutateSubProducts: builder.mutation<
      object,
      {
        data?: object;
        productId: string;
        subProductId?: string;
        method: "POST" | "PATCH" | "DELETE" | "PUT";
      }
    >({
      query: ({ data, productId, subProductId, method }) => ({
        url: `workspace/${getWorkspace()}/tool/product/${productId}/sub-product/${subProductId ? `${subProductId}` : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: [
        "single-subProduct",
        "single-product",
        "subProductList",
      ],
    }),

    //Mutate SubProduct Items
    mutateSubProductItem: builder.mutation<
      object,
      {
        data?: object;
        productId: string;
        subProductId: string;
        itemId?: string;
        method: "POST" | "PATCH" | "DELETE" | "PUT";
      }
    >({
      query: ({ data, productId, subProductId, itemId, method }) => ({
        url: `workspace/${getWorkspace()}/tool/product/${productId}/sub-product/${subProductId}/sub-product-item/${itemId ? `${itemId}` : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["single-subProductItem", "products", "subProductItems"],
    }),

    //Update Product Quantity
    updateProductQuantity: builder.mutation<
      object,
      {
        data?: object;
        productId: string;
      }
    >({
      query: ({ data, productId }) => ({
        url: `workspace/${getWorkspace()}/tool/product/${productId}/quantity`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["products", "single-product"],
    }),

    //Get Transfer tools
    getTransferTools: builder.query<
      GeneralResponse<ITransferRequest[] | ITransferRequest>,
      { filters?: string; id?: string }
    >({
      query: ({ filters, id }) => {
        const baseUrl = `workspace/${getWorkspace()}/tool/request/transfer`;
        if (id) {
          return `${baseUrl}/${id}`;
        }
        const queryParams = filters ? `${filters}` : "";

        return `${baseUrl}${queryParams}`;
      },
      providesTags: ["transferTools"],
    }),

    //Transfer Tools from one project to another Project
    transferTools: builder.mutation<
      object,
      {
        data?: object;
      }
    >({
      query: ({ data }) => ({
        url: `workspace/${getWorkspace()}/tool/request/transfer`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["transferTools"],
    }),

    //Get Return Request
    getReturnRequest: builder.query<
      GeneralResponse<IReturnRequest[] | IReturnRequest>,
      { filters?: string; id?: string; status?: ProjectToolStatus }
    >({
      query: ({ filters, id, status }) => {
        const baseUrl = `workspace/${getWorkspace()}/tool/request/return`;
        if (id) {
          return `${baseUrl}/${id}`;
        }

        let queryParams = "";
        if (filters) {
          queryParams += `${filters}`;
        }
        if (status) {
          queryParams += `&status=${status}`;
        }

        return `${baseUrl}${queryParams.toString()}`;
      },
      providesTags: ["returnRequest"],
    }),

    //Get Product Request
    getProductRequest: builder.query<
      GeneralResponse<IProductRequest[] | IProductRequest>,
      { filters?: string; id?: string; status?: ProjectToolStatus }
    >({
      query: ({ filters, id, status }) => {
        const baseUrl = `workspace/${getWorkspace()}/tool/request/product`;
        if (id) {
          return `${baseUrl}/${id}`;
        }

        let queryParams = "";
        if (filters) {
          queryParams += `${filters}`;
        }
        if (status) {
          queryParams += `&status=${status}`;
        }

        return `${baseUrl}${queryParams.toString()}`;
      },
      providesTags: ["returnRequest"],
    }),

    //Alter Status of Product Request
    alterProductRequestStatus: builder.mutation<
      GeneralResponse<IProductRequest>,
      { id: string; body: object }
    >({
      query: ({ id, body }) => ({
        url: `workspace/${getWorkspace()}/tool/request/product/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["returnRequest"],
    }),

    //Get Awaiting Request
    getAwaitingRequest: builder.query<
      GeneralResponse<IAwaitingRequest[]>,
      { filters?: string; id?: string }
    >({
      query: ({ filters, id }) => {
        const baseUrl = `workspace/${getWorkspace()}/tool/request/awaiting`;
        if (id) {
          return `${baseUrl}/${id}`;
        }

        let queryParams = "";
        if (filters) {
          queryParams += `${filters}`;
        }

        return `${baseUrl}${queryParams.toString()}`;
      },
      providesTags: ["awaitingRequest"],
    }),

    //Approve Return Request
    approveReturnRequest: builder.mutation<
      GeneralResponse<IReturnRequest>,
      { id: string; body: object }
    >({
      query: ({ id, body }) => ({
        url: `workspace/${getWorkspace()}/tool/request/return/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["returnRequest"],
    }),

    //Assign Requested Product To Project
    assignRequestedProductToProject: builder.mutation<
      GeneralResponse<IProductRequest>,
      { id: string; body: object }
    >({
      query: ({ id, body }) => ({
        url: `workspace/${getWorkspace()}/tool/request/product/${id}/assign`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["productRequest"],
    }),

    //Reject Return Request
    rejectReturnRequest: builder.mutation<
      GeneralResponse<IReturnRequest>,
      { id: string; body: object }
    >({
      query: ({ id, body }) => ({
        url: `workspace/${getWorkspace()}/tool/request/return/${id}/reject`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["returnRequest"],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetSingleProductQuery,
  useGetConsumedProductsQuery,
  useGetSingleSubProductQuery,
  useGetSubProductItemsQuery,
  useGetSingleSubProductItemQuery,
  useMutateProductsMutation,
  useMutateSubProductsMutation,
  useUpdateProductQuantityMutation,
  useMutateSubProductItemMutation,
  useTransferToolsMutation,
  useGetTransferToolsQuery,
  useGetReturnRequestQuery,
  useGetAwaitingRequestQuery,
  useGetProductRequestQuery,
  useAlterProductRequestStatusMutation,
  useApproveReturnRequestMutation,
  useAssignRequestedProductToProjectMutation,
  useRejectReturnRequestMutation,
  useGetSubProductListQuery,
  useLazyGetSubProductListQuery,
} = toolsApi;
